


















































































import {
  calculateSlotNumberFromIndex,
  ComBox,
  getCurrentSensorData,
  getSensorTypes,
  formatSensorSlotLabel,
  getSerialForSensorIndex,
  isHyDryMini,
} from "@/data/models/ComBox";
import {
  ComBoxSensorType,
  formatHumidity,
  formatResistanceDigits,
  formatTemperature,
  isBleResistanceSensorType,
  ROOM_SENSOR_INDEX,
} from "@/data/models/ComBoxSensorData";
import {
  ComBoxSensorUsage,
  formatSensorUsage,
} from "@/data/models/ControlConfig";
import { canToggleSensor } from "@/utils/combox/SensorConfigValidation";
import { getVisibleSensorIndexes } from "@/utils/combox/SensorStatusRows";

import {
  COLOR_BUTTON_GREEN,
  COLOR_LIGHT_BLUE_GRAY,
  COLOR_BUTTON_RED,
  COLOR_BUTTON_YELLOW,
} from "@/utils/colors";
import { themedFill, themedFillTextClass } from "@/utils/Theme";
import { Component, Vue, Prop } from "vue-property-decorator";

@Component
export default class SensorStatusComponent extends Vue {
  @Prop()
  comBox!: ComBox;

  @Prop({ default: true })
  canChangeSensorUsage!: boolean;

  @Prop({ default: true })
  showSensorUsage!: boolean;

  @Prop()
  needsReferenceSensor!: boolean;

  @Prop()
  dryingProgramNumber!: number;

  @Prop()
  sensorUsage!: ComBoxSensorUsage[];

  @Prop({ default: false })
  supportsAdditionalSensors!: boolean;

  @Prop({ default: () => new Map() })
  sensorLegendMetadata!: Map<number, { serialNumber?: string }>;

  buttonColor = COLOR_BUTTON_RED;

  /** The label class for the active theme: dark on the sensor's palette fill in dark mode. */
  getButtonClass(comBoxSensorUsage: ComBoxSensorUsage) {
    return themedFillTextClass(this.getLightButtonColor(comBoxSensorUsage) ?? "", this.getLightButtonClass(comBoxSensorUsage));
  }

  /** The sensor usage button's fill for the active theme (the dark palette shade in dark mode). */
  getButtonColor(comBoxSensorUsage: ComBoxSensorUsage) {
    const color = this.getLightButtonColor(comBoxSensorUsage);

    return color === undefined ? undefined : themedFill(color);
  }

  getLightButtonClass(comBoxSensorUsage: ComBoxSensorUsage) {
    let buttonClass = "";

    switch (comBoxSensorUsage) {
      case ComBoxSensorUsage.REFERENCE:
        buttonClass += "black--text";
        break;
      default:
        buttonClass += "white--text";
        break;
    }

    return buttonClass;
  }

  getLightButtonColor(comBoxSensorUsage: ComBoxSensorUsage) {
    switch (comBoxSensorUsage) {
      case ComBoxSensorUsage.MEASUREMENT:
        return COLOR_BUTTON_GREEN;
      case ComBoxSensorUsage.PROGRAM:
        return COLOR_BUTTON_YELLOW;
      case ComBoxSensorUsage.REFERENCE:
        return COLOR_LIGHT_BLUE_GRAY;
      case ComBoxSensorUsage.NOT_USED:
        return COLOR_BUTTON_GREEN;
    }
  }

  get comBoxStatus() {
    return this.comBox.comBoxStatus;
  }

  // TODO: Discuss this with Ulf
  get isSensorUsageFixed() {
    const needsReferenceSensor = this.needsReferenceSensor;
    const hasOnlyOneActiveFloorSensor =
      this.sensorTypes.filter(
        (sensorType) =>
          sensorType === ComBoxSensorType.TELAIRE ||
          sensorType === ComBoxSensorType.RADON
      ).length === 1;

    return needsReferenceSensor && hasOnlyOneActiveFloorSensor;
  }

  get totalNumberOfSensors() {
    return this.supportsAdditionalSensors ? 9 : 5;
  }

  get displaySensors() {
    return this.sensorInfo.filter((sensor) => sensor.index !== ROOM_SENSOR_INDEX);
  }

  get roomSensorInfo() {
    return this.sensorInfo.find((sensor) => sensor.index === ROOM_SENSOR_INDEX);
  }

  get sensorTypes() {
    return getSensorTypes(this.comBoxStatus);
  }

  /** True when at least one connected sensor is a material (resistance) sensor. */
  get hasMaterialSensor() {
    return this.sensorTypes.some((type) => isBleResistanceSensorType(type));
  }

  /**
   * Width of each value column (temperature / humidity / resistance). Fixed
   * integer widths so the columns line up across the (separately rendered)
   * header, room-sensor and scrollable-list rows. In the material + usage case
   * (five columns: 3 + 3 + 2 + 2 + 2 = 12) they are 2, and the compact header
   * font keeps the 10-character headers on one line; all other cases use 3.
   */
  get valueCols() {
    return this.hasMaterialSensor && this.showSensorUsage ? 2 : 3;
  }

  get isMini() {
    return isHyDryMini(this.comBox);
  }

  /** Builds sensor serial map from sensorSlotSerials for short-sensor detection. */
  get sensorSerials(): Map<number, string> {
    const result = new Map<number, string>();
    const slotSerials = this.comBoxStatus?.sensorSlotSerials;
    if (slotSerials) {
      for (let i = 0; i < this.totalNumberOfSensors; i++) {
        const serial = getSerialForSensorIndex(slotSerials, i);
        if (serial) {
          result.set(i, serial);
        }
      }
    }
    return result;
  }

  get sensorInfo() {
    const totalSensors = this.totalNumberOfSensors;
    const sensorData = getCurrentSensorData(this.comBoxStatus);
    const sensorSerials = this.sensorSerials;
    const result = [];
    const visibleSensorIndexes = getVisibleSensorIndexes(
      this.sensorTypes,
      totalSensors
    );

    for (const index of visibleSensorIndexes) {
      const isRoomSensor = index === ROOM_SENSOR_INDEX;
      const sensorSlot = calculateSlotNumberFromIndex(index);
      const sensorType = this.sensorTypes[index];
      const slotLabel = formatSensorSlotLabel(
        index,
        sensorType,
        getSerialForSensorIndex(
          this.comBoxStatus?.sensorSlotSerials,
          index,
        ),
        this.isMini,
      ) ?? `${sensorSlot}L`;
      const name = (isRoomSensor
        ? this.$t("comBoxDetail.sensorData.roomSensor")
        : this.$t("comBoxDetail.formatSensor", [slotLabel])
      ).toString();
      const usage = this.sensorUsage[index];
      const toggleAllowed = canToggleSensor(
        index,
        this.sensorUsage,
        this.dryingProgramNumber,
        this.needsReferenceSensor,
        this.sensorTypes,
        sensorSerials,
        this.isMini,
      );
      
      const showUsage = usage !== ComBoxSensorUsage.NOT_USED &&
        sensorType !== ComBoxSensorType.EMPTY &&
        sensorType !== ComBoxSensorType.UNKNOWN;
      result.push({
        canToggleUsage: toggleAllowed,
        index,
        name,
        showUsage: showUsage,
        usageDescription: formatSensorUsage(this, usage),
        usage,
        temperature: formatTemperature(this, sensorData[index]?.temperature),
        // Fall back to a dash (matching temperature/resistance) when a sensor
        // has no humidity reading, e.g. material sensors.
        humidity: formatHumidity(this, sensorData[index]) ?? "-",
        resistanceDigits: formatResistanceDigits(
          this,
          sensorData[index]?.resistanceDigits
        ),
      });
    }

    return result;
  }

  onChangeSensorUsage(index: number, isRoomSensor: boolean) {
    this.$emit("onChangeSensorUsage", index, isRoomSensor);
  }
}
